import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { RISK, fmtEur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, ArrowUpRight, FileBarChart, LineChart } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/analyses").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/business-plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/analyses/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success(lang === "it" ? "Analisi eliminata" : "Analysis deleted");
    } catch { toast.error(lang === "it" ? "Errore eliminazione" : "Delete failed"); }
  };
  const removePlan = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/business-plans/${id}`);
      setPlans((prev) => prev.filter((x) => x.id !== id));
      toast.success(lang === "it" ? "Business Plan eliminato" : "Business Plan deleted");
    } catch { toast.error(lang === "it" ? "Errore eliminazione" : "Delete failed"); }
  };

  const dateStr = (s) => s ? new Date(s).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const Spinner = () => <div className="h-40 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-slate-800 border-t-blue-600 animate-spin" /></div>;

  return (
    <div className="App bg-slate-950 min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">{t("dash_title")}</h1>
        <p className="mt-2 text-slate-300">{lang === "it"
          ? "Piattaforma unificata: analisi di bilancio storiche e business plan predittivi."
          : "Unified platform: historical balance-sheet analyses and predictive business plans."}</p>

        <Tabs defaultValue="analyses" className="mt-8">
          <TabsList data-testid="dash-tabs">
            <TabsTrigger value="analyses" data-testid="tab-analyses">{t("bp_tab_analyses")}</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">{t("bp_tab_bp")}</TabsTrigger>
          </TabsList>

          {/* Analyses */}
          <TabsContent value="analyses" className="pt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="dash-new-btn" onClick={() => navigate("/analysis/new")} className="h-10">
                <Plus className="h-4 w-4 mr-2" /> {t("dash_new")}
              </Button>
            </div>
            {items === null ? <Spinner /> : items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900 p-16 text-center" data-testid="dash-empty">
                <FileBarChart className="h-10 w-10 mx-auto text-slate-500" />
                <p className="mt-4 text-slate-400">{t("dash_empty")}</p>
                <Button className="mt-6" onClick={() => navigate("/analysis/new")} data-testid="empty-new-btn">
                  <Plus className="h-4 w-4 mr-2" /> {t("dash_new")}
                </Button>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                {items.map((it) => {
                  const rk = RISK[it.grade] || RISK.C;
                  return (
                    <div key={it.id} data-testid={`analysis-row-${it.id}`} onClick={() => navigate(`/analysis/${it.id}`)}
                      className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-4 px-4 sm:px-6 py-4 border-b border-slate-800 last:border-0 items-center cursor-pointer hover:bg-slate-800 transition-colors duration-150">
                      <div className="col-span-2 md:col-span-5">
                        <p className="font-semibold text-white">{it.company_name}</p>
                        <p className="text-xs text-slate-500">{dateStr(it.created_at)} · {it.fiscal_year}</p>
                      </div>
                      <div className="md:col-span-3"><span className={`inline-flex items-center justify-center h-8 w-8 rounded-md font-heading font-bold text-white ${rk.solid}`}>{it.grade}</span></div>
                      <div className="md:col-span-2 font-mono-fin text-sm text-slate-200">{it.score}/100</div>
                      <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid={`open-${it.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/analysis/${it.id}`); }}><ArrowUpRight className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-${it.id}`} onClick={(e) => remove(it.id, e)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Business Plans */}
          <TabsContent value="plans" className="pt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="dash-new-bp-btn" onClick={() => navigate("/business-plan/new")} className="h-10">
                <Plus className="h-4 w-4 mr-2" /> {t("bp_new")}
              </Button>
            </div>
            {plans === null ? <Spinner /> : plans.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900 p-16 text-center" data-testid="dash-bp-empty">
                <LineChart className="h-10 w-10 mx-auto text-slate-500" />
                <p className="mt-4 text-slate-400">{lang === "it" ? "Nessun business plan. Creane uno nuovo." : "No business plans yet. Create one."}</p>
                <Button className="mt-6" onClick={() => navigate("/business-plan/new")} data-testid="empty-new-bp-btn">
                  <Plus className="h-4 w-4 mr-2" /> {t("bp_new")}
                </Button>
              </div>
            ) : (
              <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
                {plans.map((p) => (
                  <div key={p.id} data-testid={`bp-row-${p.id}`} onClick={() => navigate(`/business-plan/${p.id}`)}
                    className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-4 px-4 sm:px-6 py-4 border-b border-slate-800 last:border-0 items-center cursor-pointer hover:bg-slate-800 transition-colors duration-150">
                    <div className="col-span-2 md:col-span-5">
                      <p className="font-semibold text-white">{p.company_name}</p>
                      <p className="text-xs text-slate-500">{dateStr(p.created_at)} · {p.mode === "startup" ? "Startup" : (lang === "it" ? "Da analisi" : "From analysis")}</p>
                    </div>
                    <div className="md:col-span-3 text-sm"><span className="text-slate-400">NPV </span><span className={`font-mono-fin ${(p.kpi?.npv ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtEur(p.kpi?.npv)}</span></div>
                    <div className="md:col-span-2 font-mono-fin text-sm text-slate-200">IRR {p.kpi?.irr != null ? `${p.kpi.irr}%` : "n/d"}</div>
                    <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" data-testid={`bp-open-${p.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/business-plan/${p.id}`); }}><ArrowUpRight className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" data-testid={`bp-delete-${p.id}`} onClick={(e) => removePlan(p.id, e)} className="text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
